import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  LogOut,
  ExternalLink,
  Inbox,
  Settings2,
  LayoutGrid,
  Menu,
  BarChart3,
  Newspaper,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel";
import MessagesPanel from "@/components/admin/MessagesPanel";
import ServicesPanel from "@/components/admin/ServicesPanel";
import ArticlesPanel from "@/components/admin/ArticlesPanel";
import SettingsPanel from "@/components/admin/SettingsPanel";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);
  const [active, setActive] = useState("analytics");
  const [mobileOpen, setMobileOpen] = useState(false);

  const refreshUnread = () => {
    api
      .get("/admin/messages")
      .then((r) => setUnread(r.data.unread))
      .catch(() => {});
  };

  useEffect(() => {
    refreshUnread();
  }, []);

  const NAV = [
    { id: "analytics", label: "Statistici", icon: BarChart3, testId: "admin-tab-analytics" },
    { id: "messages", label: "Mesaje de contact", icon: Inbox, testId: "admin-tab-messages", badge: unread },
    { id: "services", label: "Servicii", icon: LayoutGrid, testId: "admin-tab-services" },
    { id: "articles", label: "Articole / Blog", icon: Newspaper, testId: "admin-tab-articles" },
    { id: "settings", label: "Conținut & Date firmă", icon: Settings2, testId: "admin-tab-settings" },
  ];

  const titleFor = {
    analytics: "Statistici & Vizite",
    messages: "Mesaje de contact",
    services: "Gestionare servicii",
    articles: "Articole / Blog",
    settings: "Conținut & Date firmă",
  };

  const go = (id) => {
    setActive(id);
    setMobileOpen(false);
  };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-white/10">
        <p className="font-display font-extrabold text-xl text-white">
          SIM<span className="text-[#FF5A1F]">SERV</span>{" "}
          <span className="text-white/40 text-sm font-semibold">Admin</span>
        </p>
        <p className="text-xs text-white/40 mt-1 font-mono truncate">{user?.email}</p>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-wider text-white/30">
          Administrare
        </p>
        {NAV.map((n) => {
          const Icon = n.icon;
          const isActive = active === n.id;
          return (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              data-testid={n.testId}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 ${
                isActive
                  ? "bg-[#FF5A1F] text-white"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`}
            >
              <Icon className="w-5 h-5 shrink-0" />
              <span className="flex-1 text-left">{n.label}</span>
              {n.badge > 0 && (
                <Badge className="bg-[#DC2626] text-white h-5 min-w-5 px-1.5">
                  {n.badge}
                </Badge>
              )}
            </button>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-white/10 space-y-1">
        <Link to="/" target="_blank" className="block">
          <span className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/70 hover:bg-white/10 hover:text-white transition-colors duration-200">
            <ExternalLink className="w-5 h-5 shrink-0" /> Vezi site-ul
          </span>
        </Link>
        <button
          onClick={logout}
          data-testid="admin-logout-button"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/70 hover:bg-[#DC2626] hover:text-white transition-colors duration-200"
        >
          <LogOut className="w-5 h-5 shrink-0" /> Deconectare
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F6F9] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#0F1E36] fixed inset-y-0 left-0 z-40">
        <SidebarInner />
      </aside>

      {/* Main area */}
      <div className="flex-1 lg:ml-64 min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden bg-[#0F1E36] text-white sticky top-0 z-30 flex items-center justify-between px-4 h-14">
          <p className="font-display font-extrabold">
            SIM<span className="text-[#FF5A1F]">SERV</span> Admin
          </p>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="admin-mobile-menu-button"
                className="text-white hover:bg-white/10"
              >
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 bg-[#0F1E36] border-none text-white">
              <SidebarInner />
            </SheetContent>
          </Sheet>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0F1E36] mb-6">
            {titleFor[active]}
          </h1>
          {active === "analytics" && <AnalyticsPanel />}
          {active === "messages" && <MessagesPanel onChange={refreshUnread} />}
          {active === "services" && <ServicesPanel />}
          {active === "articles" && <ArticlesPanel />}
          {active === "settings" && <SettingsPanel />}
        </main>
      </div>
    </div>
  );
}
